import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

function groupReactions(reactions = []) {
  const map = {};
  for (const r of reactions) {
    map[r.emoji] = (map[r.emoji] || 0) + 1;
  }
  return Object.entries(map).map(([emoji, count]) => ({ emoji, count }));
}

export default function MessageBubble({ message, isOwn, onAction, isEditing, onEditSave, onEditCancel }) {
  const grouped = groupReactions(message.reactions);
  const [editText, setEditText] = useState(message.content);

  useEffect(() => {
    if (isEditing) setEditText(message.content);
  }, [isEditing, message.content]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        my: 0.5,
        px: 1,
      }}
    >
      <Box
        onClick={() => { if (!isEditing) onAction(message); }}
        sx={{
          maxWidth: { xs: '85%', sm: '75%' },
          backgroundColor: isOwn ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.08)',
          borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          px: 1.5,
          py: 1,
          cursor: isEditing ? 'default' : 'pointer',
          userSelect: 'text',
        }}
      >
        {!isOwn && (
          <Typography variant="caption" sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 700, display: 'block', mb: 0.25 }}>
            {message.sender_username}
          </Typography>
        )}

        {message.reply_to && (
          <Box sx={{ borderLeft: '3px solid rgba(255,255,255,0.35)', pl: 1, mb: 0.75, opacity: 0.75 }}>
            <Typography variant="caption" sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 700, display: 'block' }}>
              @{message.reply_to.sender_username}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
              {message.reply_to.content}
            </Typography>
          </Box>
        )}

        {isEditing ? (
          <Box>
            <TextField
              fullWidth
              multiline
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (editText.trim()) onEditSave(editText.trim()); }
                if (e.key === 'Escape') onEditCancel();
              }}
              size="small"
              inputProps={{ maxLength: 2000 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
              <IconButton
                data-testid="edit-cancel"
                onClick={onEditCancel}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 44, minHeight: 44 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <IconButton
                data-testid="edit-save"
                onClick={() => { if (editText.trim()) onEditSave(editText.trim()); }}
                size="small"
                disabled={!editText.trim()}
                sx={{ color: DEFAULT_THEME_COLOR, minWidth: 44, minHeight: 44 }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </Typography>
        )}

        {!isEditing && (
          <Typography variant="caption" sx={{ color: isOwn ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.4)', display: 'block', textAlign: 'right', mt: 0.25, fontSize: '0.65rem' }}>
            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {message.edited_at && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>(edited)</span>}
          </Typography>
        )}
      </Box>

      {grouped.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, px: 0.5 }}>
          {grouped.map(({ emoji, count }) => (
            <Box
              key={emoji}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                borderRadius: 3,
                px: 0.75,
                py: 0.25,
                fontSize: '0.75rem',
                color: 'white',
              }}
            >
              {emoji} {count}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
